const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A calendar date — year, month, day, nothing else: no time of day, no timezone. This is the actual
 * shape of a birth date, unlike a JavaScript `Date`, which is always an instant. Parsing
 * `"1942-02-03"` through `new Date(...)` reads it as UTC midnight, which renders a day earlier in any
 * timezone west of Greenwich; building one from a calendar date must therefore always go through local
 * year/month/day components (`getFullYear`/`getMonth`/`getDate`), never through ISO-string parsing.
 */
class PlainDate {
	constructor(
		public readonly year: number,
		public readonly month: number,
		public readonly day: number,
	) {
		Object.freeze(this);
	}

	static fromApi(value: string): PlainDate | null {
		const match = CALENDAR_DATE_PATTERN.exec(value);

		if (match === null) {
			return null;
		}

		const [, year, month, day] = match;

		return new PlainDate(Number(year), Number(month), Number(day));
	}

	static fromCalendar(value: Date): PlainDate {
		return new PlainDate(value.getFullYear(), value.getMonth() + 1, value.getDate());
	}

	static today(now: Date = new Date()): PlainDate {
		return PlainDate.fromCalendar(now);
	}

	toApi(): string {
		return `${String(this.year).padStart(4, '0')}-${String(this.month).padStart(2, '0')}-${String(this.day).padStart(2, '0')}`;
	}

	toDisplay(): string {
		return `${String(this.day).padStart(2, '0')}/${String(this.month).padStart(2, '0')}/${String(this.year).padStart(4, '0')}`;
	}

	toCalendar(): Date {
		return new Date(this.year, this.month - 1, this.day);
	}

	ageInYears(today: Date = new Date()): number {
		const currentYear = today.getFullYear();
		const currentMonth = today.getMonth() + 1;
		const currentDay = today.getDate();

		let age = currentYear - this.year;

		const birthdayNotYetReachedThisYear =
			currentMonth < this.month || (currentMonth === this.month && currentDay < this.day);

		if (birthdayNotYetReachedThisYear) {
			age -= 1;
		}

		return age;
	}
}

export default PlainDate;
